import React, { useEffect, useState } from "react";

import {
  Stack,
  Button,
  useDisclosure,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";

import NewTicketModalHelpSupportComponent from "./components/NewTicketModalHelpSupportComponent";
import ViewTicketDetailModalHelpSupportComponent from "./components/ViewTicketDetailModalHelpSupportComponent";

import TablePagination from "components/TablePagination/TablePagination";

import GetSupportTickets from "services/GetSupportTickets";
import formatDate from "utils/utils";

import button_plus_icon from "../../assets/images/icons/button_plus_dark_icon.png";
import useDocumentTitle from "hooks/useDocumentTitle";
import ActiveInactiveTags from "components/Tags/ActiveInactiveTags";

export default function Supplier() {
  useDocumentTitle("Help & Support");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isOpenViewDetail,
    onOpen: onOpenViewDetail,
    onClose: onCloseViewDetail,
  } = useDisclosure();

  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [tickets, setTickets] = useState([]);

  const [refreshSupportTickets, setRefreshSupportTickets] = useState(false);

  const [viewTicketDetail, setViewTicketDetail] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    const fetchTickets = async () => {
      const params = {
        page_size: pageSize,
        page_number: currentPage,
      };

      const res = await GetSupportTickets(params);

      setTickets(res.data.data);
      setTotalPages(res.data.total_pages);
      setTotalRecords(res.data.total_records);
      setIsLoading(false);
    };

    fetchTickets();
  }, [refreshSupportTickets, currentPage, pageSize]);

  const handleNewTicketClick = () => {
    onOpen();
  };

  const handleViewTicketDetailClick = (data) => {
    setViewTicketDetail(data);
    onOpenViewDetail();
  };

  return (
    <div className=" ">
      <Stack spacing={"35px"}>
        <ViewTicketDetailModalHelpSupportComponent
          isOpen={isOpenViewDetail}
          onClose={onCloseViewDetail}
          data={viewTicketDetail}
        />
        <NewTicketModalHelpSupportComponent
          isOpen={isOpen}
          onClose={onClose}
          setRefreshSupportTickets={setRefreshSupportTickets}
        />
        <div>
          <h1 className="font-raleway  ">Help & Support</h1>
        </div>

        <p className="light-paragraph-open-sans">
          Having trouble or need assistance? Our support team is here to help
          you every step of the way.
        </p>

        <div className="px-6 py-4">
          <h4>Tips for Faster Resolution</h4>

          <p className="mt-4">
            Follow these tips to help us resolve your issues more efficiently:
          </p>
          <ol className="light-paragraph-open-sans">
            <li>
              1. <b>Be Specific:</b> Clearly describe your issue or question.
              The more details, the better!
            </li>
            <li>
              2. <b>Attach Files:</b> If possible, attach screenshots or
              relevant files to help us understand your issue.
            </li>
            <li>
              3. <b>Stay Updated:</b> Keep an eye on your ticket status and
              respond promptly to any questions from our support team.
            </li>
          </ol>
        </div>

        <div className="border border-gray-300  px-6 py-4">
          <h4>Open a New Support Ticket</h4>
          <p className="mt-4 mb-8">
            Encountered an issue or have a question? Let's get it sorted! Fill
            out the form below with all the details. The more information you
            provide, the better we can assist you. Don’t forget to attach any
            relevant screenshots or files!
          </p>

          <div className="w-36 h-12 ">
            <Button variant="outline" onClick={handleNewTicketClick}>
              <img className="mr-1" src={button_plus_icon} />
              New Ticket
            </Button>
          </div>
        </div>

        <div className="border border-gray-300   pt-4">
          <div className="px-6">
            <h4>Your Support Tickets</h4>
          </div>

          {isLoading ? (
            <div className="flex flex-row justify-center   h-[34rem] ">
              <Spinner className="my-auto" size="xl" />
            </div>
          ) : (
            <>
              {tickets.length !== 0 ? (
                <>
                  <div className="px-6 mb-8 mt-8 ">
                    <p className="light-paragraph-open-sans">
                      Keep track of all your support requests in one place.
                      Below, you can see a list of all your submitted tickets
                      along with their current status (e.g., Open, In Progress,
                      Resolved). Click on any ticket to view more details and
                      see responses from our support team.
                    </p>
                  </div>
                  <div className="overflow-x-auto    ">
                    <table className=" w-full  border primary-border-color ">
                      <thead>
                        <tr className="bg-[#e9e9e9] h-[2.63rem]">
                          <th className="pl-4 py-1.5  ">
                            <p className="table-heading">Subject</p>
                          </th>

                          <th className="py-1.5 ">
                            <p className="table-heading">Description</p>
                          </th>
                          <th className="py-1.5 ">
                            <p className="table-heading">Status</p>
                          </th>
                          <th className="py-1.5 ">
                            <p className="table-heading">Submitted On</p>
                          </th>
                          <th>
                            <p className="table-heading"></p>
                          </th>
                        </tr>
                      </thead>
                      <tbody className=" ">
                        {tickets?.map((data, index) => {
                          return (
                            <tr
                              className="border-x primary-border-color hover:bg-gray-100   h-[3.13rem] "
                              key={index}
                            >
                              <td className="pl-2 py-2.5    ">
                                <p className="table-text">
                                  {data.support_ticket_title}
                                </p>
                              </td>
                              <td className="table-text-light overflow-ellipsis overflow-hidden max-w-60 xl:max-w-lg whitespace-nowrap ">
                                {data.support_ticket_description}
                              </td>
                              <td className="table-text-light">
                                <ActiveInactiveTags
                                  is_active={data.support_ticket_status}
                                />
                              </td>
                              <td className="table-text-light">
                                {formatDate(data.created_on)}
                              </td>
                              <td className="table-text-light">
                                <Button
                                  variant="ghost"
                                  onClick={() =>
                                    handleViewTicketDetailClick(data)
                                  }
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <TablePagination
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    totalRecords={totalRecords}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                  />
                </>
              ) : (
                <div className="px-6 mb-8 mt-8 text-center">
                  <p className="light-paragraph-open-sans">
                    There are currently no open support tickets. If you need
                    assistance, please click on 'New Ticket' to create a support
                    request and receive personalized help.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col"></div>
      </Stack>
    </div>
  );
}
